import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const loginAsAdmin = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      await login('admin@playkids.com.br', 'admin123');
      navigate('/gerenciamento-colonia');
    } catch (error: any) {
      console.error('[AUTH] Atalho administrativo não realizado', {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      setErrorMessage(error.message || 'Ocorreu um erro ao fazer login. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, login, navigate]);

  useEffect(() => {
    const handleAdminShortcut = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        void loginAsAdmin();
      }
    };

    window.addEventListener('keydown', handleAdminShortcut);
    return () => window.removeEventListener('keydown', handleAdminShortcut);
  }, [loginAsAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      console.warn('[AUTH] Tentativa bloqueada', {
        reason: 'missing_credentials',
        timestamp: new Date().toISOString()
      });
      setErrorMessage('Por favor, preencha todos os campos.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      await login(email, password);
      navigate('/gerenciamento-colonia');
    } catch (error: any) {
      console.error('[AUTH] Login não realizado', {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      setErrorMessage(error.message || 'Ocorreu um erro ao fazer login. Tente novamente.');
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img 
              src="/logo.png" 
              alt="Colonia de Ferias UBA Clube 2026" 
              className="w-full max-w-[280px] max-h-[150px] object-contain mx-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-800 font-display">
              Area Administrativa
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input pl-10"
                  placeholder="seu@email.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="form-input pl-10 pr-12"
                  placeholder="********"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {errorMessage && (
              <div className="text-danger-600 text-sm text-center bg-danger-50 border border-danger-200 rounded-lg p-3">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={!email || !password || isLoading}
              className="w-full btn-primary disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
